"use client";
import CheckBulletItem from "@/components/common/checkBulletItem/checkBulletItem.component";
import {
  PillTabScroller,
  pillTabClass,
  WhatsAppPhone,
} from "@/components/marketing";
import { useRovingTabs } from "@/hooks/useRovingTabs.hook";
import { useId } from "react";
import { channelPreview, type CampaignChannelKind } from "../../data";
import { EmailPreview, NotificationPreview, SmsPreview } from "./components";

const { shop, whatsApp } = channelPreview;

const Device = ({ kind }: { kind: CampaignChannelKind }) => {
  switch (kind) {
    case "whatsapp":
      return (
        <WhatsAppPhone
          chat={{
            initial: shop.initial,
            shop: shop.name,
            subtitle: whatsApp.subtitle,
          }}
          messages={[whatsApp.message]}
        />
      );
    case "email":
      return <EmailPreview />;
    case "notification":
      return <NotificationPreview />;
    case "sms":
      return <SmsPreview />;
  }
};

// "See a campaign on every channel": one-line pill tabs pick a channel, and
// the panel shows the same campaign as the customer sees it there, beside
// what the channel is good for.
const ChannelPreview = () => {
  const tabs = useRovingTabs(channelPreview.channels.length);
  const baseId = useId();

  return (
    <figure role="group" aria-label={channelPreview.ariaLabel} className="m-0">
      <PillTabScroller ariaLabel={channelPreview.tablistLabel}>
        {channelPreview.channels.map((channel, index) => {
          const isActive = index === tabs.activeIndex;
          return (
            <button
              key={channel.kind}
              type="button"
              role="tab"
              id={`${baseId}-tab-${index}`}
              aria-controls={`${baseId}-panel-${index}`}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => tabs.select(index)}
              onKeyDown={(event) => tabs.onTabKeyDown(event, index)}
              className={pillTabClass(isActive)}
            >
              {channel.tab}
            </button>
          );
        })}
      </PillTabScroller>

      {channelPreview.channels.map((channel, index) => (
        <div
          key={channel.kind}
          role="tabpanel"
          id={`${baseId}-panel-${index}`}
          aria-labelledby={`${baseId}-tab-${index}`}
          hidden={index !== tabs.activeIndex}
          className="mx-auto mt-8 max-w-[860px]"
        >
          <div className="grid items-center justify-items-center gap-8 md:grid-cols-[320px_minmax(0,1fr)] md:justify-items-stretch md:gap-12">
            <div className="flex w-full justify-center">
              <Device kind={channel.kind} />
            </div>
            <div className="grid gap-3">
              <h3 className="text-[1.5rem] leading-tight font-bold text-ink">
                {channel.title}
              </h3>
              <p className="text-base leading-relaxed text-ink-muted">
                {channel.text}
              </p>
              <ul className="grid gap-2">
                {channel.points.map((point) => (
                  <CheckBulletItem key={point} className="text-base">
                    {point}
                  </CheckBulletItem>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </figure>
  );
};

export default ChannelPreview;
